<template>
  <div
    class="accordion-panel"
    :class="{
      expanded: expanded,
      disabled: disabled,
      completed: useOmwIcon,
    }"
  >
    <div
      class="accordion-header"
      :class="{ disabled: disabled }"
      @click="$emit('toggle', 'verifyWill')"
    >
      <div class="header-main">
        <div class="header-icon">
          <img
            v-if="useOmwIcon"
            :src="whitelabel.images.omwIcon"
            alt=""
            width="24"
            height="24"
          />
          <v-icon v-else :color="headerIconColor" size="24">
            {{ headerIcon }}
          </v-icon>
        </div>

        <div class="header-title">
          <h3 :style="{ color: headerTitleColor }">{{ headerTitle }}</h3>
        </div>

        <div class="header-chevron">
          <v-icon color="#666" size="20">
            {{ expanded ? icons.mdiChevronUp : icons.mdiChevronDown }}
          </v-icon>
        </div>
      </div>
    </div>

    <div class="expanded-container" :class="{ expanded: expanded }">
      <div class="status-container">
        <p class="status-text">
          Status:
          <span
            class="status-value"
            :style="{ color: latestVersionStatusDetails.color }"
            >{{ latestVersionStatusDetails.status }}</span
          >
        </p>
      </div>

      <div class="accordion-content">
        <div class="content-wrapper">
          <div class="section-content">
            <div
              class="content-margins"
              v-if="latestVersionStatusDetails.status === 'Not uploaded'"
            >
              <p>
                You can now <strong>sign your will and upload it</strong> to our
                platform.
              </p>
              <p>
                We will <strong>verify</strong> your will and send you
                communication to set your will location
              </p>

              <div class="action-buttons">
                <GradientButton
                  :title="'Upload to verify Will'"
                  @gradientButtonClicked="$emit('willVerification')"
                />
              </div>
            </div>
            <div
              class="content-margins"
              v-else-if="
                latestVersionStatusDetails.status === 'Pending validation'
              "
            >
              <p>
                Your will has been
                <strong>uploaded and is being verified</strong>. You will be
                notified when the <strong>validation is complete</strong>.
              </p>
              <div class="action-buttons">
                <GradientButton
                  :fill="false"
                  :title="'Upload again'"
                  @gradientButtonClicked="$emit('willVerification')"
                />
              </div>
            </div>
            <div
              class="content-margins"
              v-else-if="latestVersionStatusDetails.status === 'Valid'"
            >
              <p class="mb-1">
                <strong>Version:</strong> {{ latestVersion.version }}
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <strong>Created</strong>
                {{ formattedVersionDate }}
              </p>
              <div class="action-buttons">
                <GradientButton
                  :title="'View your validated will'"
                  @gradientButtonClicked="
                    $emit('viewUploadedWill', latestUpload.id)
                  "
                />
              </div>
            </div>
            <div
              class="content-margins"
              v-else-if="latestVersionStatusDetails.status === 'Invalid'"
            >
              <p>
                Your will has been <strong>reviewed</strong>, unfortunately it
                <strong>does not currently meet the requirements</strong>.
              </p>
              <div class="action-buttons">
                <GradientButton
                  :title="'Re-upload your will'"
                  @gradientButtonClicked="$emit('willVerification')"
                />
              </div>
            </div>
            <div
              class="content-margins"
              v-else-if="
                latestVersionStatusDetails.status === 'Validity inconclusive'
              "
            >
              <p>
                Your will has been <strong>reviewed</strong>, unfortunately the
                <strong>validity was inconclusive</strong>.
              </p>
              <div class="action-buttons">
                <GradientButton
                  :title="'Re-upload your will'"
                  @gradientButtonClicked="$emit('willVerification')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {
    mdiChevronDown,
    mdiChevronUp,
    mdiCheckCircle,
    mdiAlertCircleOutline,
    mdiInformationOutline,
  } from '@mdi/js';
  import {
    latestWillVersion,
    latestUploadedWill,
    verificationStatus,
  } from '../../../constants/js/will-verification';

  export default {
    name: 'VerifyWill',
    props: {
      expanded: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      completed: {
        type: Boolean,
        default: false,
      },
      testator: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        icons: {
          mdiChevronDown: mdiChevronDown,
          mdiChevronUp: mdiChevronUp,
          mdiCheckCircle: mdiCheckCircle,
          mdiAlertCircleOutline: mdiAlertCircleOutline,
          mdiInformationOutline: mdiInformationOutline,
        },
      };
    },
    computed: {
      latestVersion() {
        return latestWillVersion(this.testator.willVersions) || {};
      },
      latestUpload() {
        return latestUploadedWill(this.testator.willVersions);
      },
      latestVersionStatusDetails() {
        return verificationStatus(this.latestVersion?.status, this.icons);
      },
      formattedVersionDate() {
        if (!this.latestVersion?.createdAt) return '';
        return new Date(this.latestVersion.createdAt)
          .toISOString()
          .split('T')[0];
      },
      useOmwIcon() {
        return this.latestVersion?.status === 'VALID';
      },
      headerIcon() {
        const status = this.latestVersion?.status;
        if (status === 'VALID') return this.icons.mdiCheckCircle;
        if (status === 'INVALID' || status === 'VALIDITY_INCONCLUSIVE')
          return this.icons.mdiAlertCircleOutline;
        if (status === 'PENDING') return this.icons.mdiCheckCircle;
        return this.icons.mdiCheckCircle;
      },
      headerIconColor() {
        const status = this.latestVersion?.status;
        if (status === 'VALID') return this.whitelabel.colors.leftPanelGreen;
        if (status === 'INVALID' || status === 'VALIDITY_INCONCLUSIVE')
          return '#FF6F61';
        if (status === 'PENDING')
          return this.whitelabel.colors.leftPanelIconGrey;
        return this.whitelabel.colors.leftPanelIconGrey;
      },
      headerTitleColor() {
        if (this.latestVersion?.status === 'VALID')
          return this.whitelabel.colors.leftPanelGreen;
        return undefined;
      },
      headerTitle() {
        if (this.latestVersion?.status === 'VALID') {
          const date = this.latestVersion.updatedAt
            ? new Date(this.latestVersion.updatedAt).toISOString().split('T')[0]
            : '';
          return `WILL VALIDATED - ${date}`;
        }
        return 'VALIDATE MY WILL';
      },
    },
  };
</script>
